import React, { useState, useEffect, useMemo } from "react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";
import { loadingCard } from "../../../helpers/loading";

ChartJS.register(ArcElement, Tooltip, Legend);

interface RawDataItem {
  id?: number;
  resource_name: string;
  resource_type_key: string;
  resource_category: string;
  case_manager: string;
}

interface ResourcePieChartProps {
  data: any;
}

export const ResourceByTypePieChart = ({ data }: ResourcePieChartProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [typeGroups, setTypeGroups] = useState<any[]>();

  useEffect(() => {
    if (data) {
      const { resources } = data;
      const [typeGroups] = groupData(resources);
      setTypeGroups(typeGroups);
    }
    setIsLoading(false);
  }, [data]);

  function groupData(data: RawDataItem[]) {
    let counts: Record<string, { name: string; count: number }> = {};
    data.forEach((item) => {
      const key = item.resource_type_key ?? item.resource_type_key ?? "NO TYPE";
      const name =
        item.resource_type_key && item.resource_type_key === "consumable"
          ? "CONSUMABLE"
          : item.resource_type_key === "nonconsumable"
            ? "NON-CONSUMABLE"
            : "NO TYPE";
      if (!counts[key]) {
        counts[key] = { name, count: 0 };
      }
      counts[key].count += 1;
    });
    const typeCount = Object.entries(counts)
      .map(([id, { name, count }]) => ({ id, label: name, numberAssignments: count }))
      .sort((a: any, b: any) => a.label - b.label);

    return [typeCount];
  }

  const chartData = useMemo(() => {
    const key = typeGroups;

    return {
      labels: key && key.length > 0 ? key.map((item) => item.label) : ["No data available"],
      datasets: [
        {
          label: "# Assignments",
          data: key && key.length > 0 ? key.map((item) => item.numberAssignments) : [0],
          backgroundColor: ["#ed2f2f", "#DA7E39", "#B2BEB5", "#5A7082", "#5D82A0", "#F4A259", "#B5B2A4", "#A0C1B8", "#E07A5F", "#3D5A80"],
          borderWidth: 1,
        },
      ],
    };
  }, [typeGroups]);

  const options = {
    plugins: {
      maintainAspectRation: false,
      title: {
        display: true,
        text: `Assignments by Type`,
        color: "#5a7082",
        font: {
          size: 18,
          weight: 500,
        },
      },
      legend: {
        display: true,
        position: "top" as const,
        align: "center" as const,
        labels: {
          boxWidth: 25,
          font: {
            size: 14,
          },
          padding: 15,
        },
      },
    },
    layout: {
      padding: 0,
    },
  };

  if (isLoading) {
    return loadingCard();
  }

  return (
    <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Pie data={chartData} options={options} width={350} height={350} style={{ maxHeight: "100%", maxWidth: "100%" }} />
    </div>
  );
};
